import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { withForm } from '@/shared/ui/form';

import type { TriggerConditions } from '../../hooks';
import { signalAlertFormOptions } from '../../model';
import { SignalAlertFormPanel } from './signal-alert-form-panel';
import {
  type PreviewState,
  SignalAlertPreviewPanel,
} from './signal-alert-preview-panel';
import { useSignalAlertPreviewIssues } from './use-signal-alert-preview-issues';

// Preview 값들을 받아 렌더링하는 내부 컴포넌트
function PreviewSection({
  workspaceId,
  signals,
  duration,
  filter,
  conditions,
}: {
  workspaceId: string;
  signals: string[];
  duration: number;
  filter: StructuredFilter;
  conditions: TriggerConditions;
}) {
  // preview 이슈 데이터 조회
  const { previewIssues, isLoading } = useSignalAlertPreviewIssues({
    workspaceId,
    signals,
    duration,
    filter,
  });

  // PreviewState 계산 (빈 배열 = all issues이므로 항상 preview 표시)
  const preview: PreviewState = useMemo(() => {
    if (isLoading) return { status: 'loading' };
    return { status: 'loaded', data: previewIssues };
  }, [isLoading, previewIssues]);

  return (
    <SignalAlertPreviewPanel
      preview={preview}
      duration={duration}
      conditions={conditions}
    />
  );
}

export const SignalAlertFormContent = withForm({
  ...signalAlertFormOptions,
  props: {
    workspaceId: '',
    renderFooter: undefined as (() => ReactNode) | undefined,
    existingTitles: new Set<string>(),
  },
  render: function SignalAlertFormContent({
    form,
    workspaceId,
    renderFooter,
    existingTitles,
  }) {
    return (
      <div className="grid grid-cols-[1fr_2fr] gap-6 px-4 pb-6 h-full">
        <SignalAlertFormPanel
          form={form}
          workspaceId={workspaceId}
          renderFooter={renderFooter}
          existingTitles={existingTitles}
        />
        {/* form.Subscribe로 preview에 필요한 값들 구독 */}
        <form.Subscribe
          selector={(state) => ({
            signals: state.values.signals,
            duration: state.values.duration,
            filter: state.values.filter ?? {},
            feedbackCountCondition: state.values.feedbackCountCondition,
            changeRateCondition: state.values.changeRateCondition,
            conditionOperator: state.values.conditionOperator,
          })}
        >
          {({
            signals,
            duration,
            filter,
            feedbackCountCondition,
            changeRateCondition,
            conditionOperator,
          }) => (
            <PreviewSection
              workspaceId={workspaceId}
              signals={signals}
              duration={duration}
              filter={filter}
              conditions={{
                feedbackCountCondition,
                changeRateCondition,
                conditionOperator,
              }}
            />
          )}
        </form.Subscribe>
      </div>
    );
  },
});
